import React from 'react';

import {
  NetworkMode,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { History, createBrowserHistory } from 'history';
import { IntlShape, RawIntlProvider } from 'react-intl';
import { Router } from 'react-router';
import {
  useHydrateBundleProps,
  useUpdateWhiteLabelingBundleProps,
  type BundleProps,
} from 'signer-app/bundle-props';
import {
  FeatureFlagProvider,
  SignerAppFeatureFlags,
  FeatureFlagProvider as SignerFeatureFlagProvider,
} from 'signer-app/context/feature-flags';
import {
  SignerAppClient,
  SignerAppClientProvider,
} from 'signer-app/context/signer-app-client';
import ErrorBoundary from 'signer-app/parts/error-boundary';
import { NotificationBannerProvider } from 'signer-app/parts/notifications/notification-banners';
import TouchProvider from 'signer-app/parts/touch-provider';
import { AriaLiveContextProvider } from 'signer-app/utils/use-aria-live';

export type SignerAppProps = React.PropsWithChildren<
  BundleProps & {
    suspenseFallback?: React.ReactNode;
    featureFlags: SignerAppFeatureFlags;
    signerAppClient: SignerAppClient;
    history?: History;
    intl: IntlShape;
  }
>;
const defaultFallback = <div />;
export const dummyEmbeddedData: BundleProps['embeddedData'] = {
  typeCode: 'S',
  signatureId: '',
  parentUrl: '',
  redirectUrl: '',
  isEmbeddedSigning: false,
};

export default function SignerApp({
  children,
  featureFlags,
  suspenseFallback = defaultFallback,
  signerAppClient,
  history: maybeHistory,
  intl,
  whiteLabelStyles,
  hasCustomStyles,
  customLogoUri,
  signerAccountId,
  signerDbxAccountId,
  tsmGuid,
  embeddedData,
  signers,
}: SignerAppProps) {
  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        // Although networkMode is a great idea in theory, headless selenium tests trick react-query
        // into thinking there is no network connection and all queries are 'paused'
        // https://tanstack.com/query/v4/docs/react/guides/network-mode
        defaultOptions: {
          queries: {
            cacheTime: Infinity,
            staleTime: Infinity,
            networkMode: 'always' as NetworkMode,
            retry: false, // don't retry on failure
            refetchOnWindowFocus: false, // don't refetch when a window/tab is refocused
            refetchOnReconnect: false, // don't refetch if the user regains internet connection
          },
        },
      }),
  );
  const history = React.useMemo(() => {
    if (maybeHistory) {
      return maybeHistory;
    }
    return createBrowserHistory();
  }, [maybeHistory]);
  useHydrateBundleProps({
    whiteLabelStyles,
    hasCustomStyles,
    customLogoUri,
    signerAccountId,
    signerDbxAccountId,
    tsmGuid,
    embeddedData,
    signers,
  });

  // Ensure that whiteLabelStyles and customLogoUri are kept up to date for
  // the purpose of dynamic updates served from the white-labeling editor.
  useUpdateWhiteLabelingBundleProps(
    { whiteLabelStyles, customLogoUri },
    { enabled: hasCustomStyles },
  );

  return (
    <React.Suspense fallback={suspenseFallback}>
      <QueryClientProvider client={queryClient}>
        <Router history={history}>
          <SignerAppClientProvider client={signerAppClient}>
            <NotificationBannerProvider>
              <TouchProvider>
                <RawIntlProvider value={intl}>
                  <FeatureFlagProvider featureFlags={featureFlags || {}}>
                    <SignerFeatureFlagProvider
                      featureFlags={featureFlags || {}}
                    >
                      <ErrorBoundary>
                        <AriaLiveContextProvider>
                          {children}
                        </AriaLiveContextProvider>
                      </ErrorBoundary>
                    </SignerFeatureFlagProvider>
                  </FeatureFlagProvider>
                </RawIntlProvider>
              </TouchProvider>
            </NotificationBannerProvider>
          </SignerAppClientProvider>
        </Router>
      </QueryClientProvider>
    </React.Suspense>
  );
}
